// This file will be populated with the content from the original useHoldingsController.ts 

import { useState, useEffect, useCallback } from "react";
import { useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { getHoldings, updateHolding, deleteHolding } from "@/api/Holdings";
import { HoldingWithInstrumentAndAccount } from "@/types";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";

export type ModalType = "add" | "edit" | "view" | "delete" | null;

export function useHoldingsController() {
  const [holdings, setHoldings] = useState<HoldingWithInstrumentAndAccount[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedHolding, setSelectedHolding] =
    useState<HoldingWithInstrumentAndAccount | null>(null);

  const selectedFund = useAtomValue(SelectedFundAtom);
  const selectedReportingPeriod = useAtomValue(SelectedReportingPeriodAtom);
  // Fetch holdings - mock implementation for now
  const fetchHoldings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (!selectedFund) {
        setHoldings([]);
        setIsLoading(false);
        return;
      }

      if (!selectedReportingPeriod) {
        setHoldings([]);
        setIsLoading(false);
        return;
      }
      const result = await getHoldings(selectedFund.fundId, parseInt(selectedReportingPeriod.reportingPeriodId) || undefined);

      // Wait a short time to simulate API call
      setTimeout(() => {
        setHoldings(result);
        setIsLoading(false);
      }, 1000);
    } catch (err) {
      console.error("Error fetching holdings:", err);
      setError("Failed to fetch holdings");
      setIsLoading(false);
    }
  }, [selectedFund, selectedReportingPeriod]);

  // Fetch holdings on component mount and when selected fund changes
  useEffect(() => {
    fetchHoldings();
  }, [fetchHoldings]);

  // Filter holdings based on search query
  const filteredHoldings = holdings.filter((holding) => {
    // if (!searchQuery.trim()) return true;

    // const search = searchQuery.toLowerCase();
    // return (
    //   holding.securityName.toLowerCase().includes(search) ||
    //   (holding.ticker && holding.ticker.toLowerCase().includes(search)) ||
    //   holding.assetClass.toLowerCase().includes(search) ||
    //   holding.marketValue.toString().includes(search)
    // );

    return true;
  });

  // Handle holding confirmation status update
  const confirmHolding = async (
    holding: HoldingWithInstrumentAndAccount,
    isConfirmed: boolean
  ) => {
    try {
      const updatedHolding = await updateHolding({
        ...holding,
        holdingId: holding.holdingId,
        isConfirmed,
      });

      if (updatedHolding) {
        // Update the local state with the updated holding
        setHoldings((prev) =>
          prev.map((h) =>
            h.holdingId === holding.holdingId ? { ...h, isConfirmed } : h
          )
        );
      }
    } catch (error) {
      console.error("Error updating holding confirmation status:", error);
    }
  };

  // Handle holding deletion
  const deleteHoldingHandler = async (holding: HoldingWithInstrumentAndAccount) => {
    if (!selectedReportingPeriod) {
      console.error("No reporting period selected");
      return;
    }

    try {
      const success = await deleteHolding(
        holding.holdingId,
        parseInt(selectedReportingPeriod.reportingPeriodId)
      );

      if (success) {
        // Remove the deleted holding from the local state
        setHoldings((prev) =>
          prev.filter((h) => h.holdingId !== holding.holdingId)
        );
      }
    } catch (error) {
      console.error("Error deleting holding:", error);
    }
  };

  // Modal management
  const openModal = (
    type: ModalType,
    holding: HoldingWithInstrumentAndAccount | null = null
  ) => {
    setModalType(type);
    setSelectedHolding(holding);
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedHolding(null);
  };

  return {
    holdings,
    filteredHoldings,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    modalType,
    selectedHolding,
    openModal,
    closeModal,
    fetchHoldings,
    confirmHolding,
    deleteHoldingHandler,
  };
} 